/* eslint-disable @typescript-eslint/no-explicit-any */
import apiClient from './api';
import type { Post, Comment, UserPublicProfile } from '../types';

// ─── Normalizers (snake_case backend → camelCase frontend) ────────────────────

function normalizePost(p: any): Post {
  return {
    id: String(p.id),
    userId: String(p.user_id),
    userName: p.user_name || 'User',
    userAvatar: p.user_avatar || undefined,
    content: p.content,
    imageUrl: p.image_url || undefined,
    likesCount: p.likes_count ?? 0,
    commentsCount: p.comments_count ?? 0,
    isLiked: p.is_liked ?? false,
    createdAt: p.created_at,
  };
}

function normalizeComment(c: any): Comment {
  return {
    id: String(c.id),
    postId: String(c.post_id),
    userId: String(c.user_id),
    userName: c.user_name || 'User',
    userAvatar: c.user_avatar || undefined,
    content: c.content,
    createdAt: c.created_at,
  };
}

function normalizeProfile(p: any): UserPublicProfile {
  return {
    id: String(p.user_id),
    name: p.name || 'User',
    avatar: p.avatar || undefined,
    bio: p.bio || undefined,
    followersCount: p.followers_count ?? 0,
    followingCount: p.following_count ?? 0,
    isFollowing: p.is_following ?? false,
    postsCount: p.post_count ?? 0,
  };
}

// ─── API ──────────────────────────────────────────────────────────────────────

export const socialApi = {
  getFeed: async (page = 1, pageSize = 20): Promise<{ posts: Post[]; total: number }> => {
    const response = await apiClient.get('/community/feed', {
      params: { page, page_size: pageSize },
    });
    const data = response.data;
    const rawItems: any[] = data.items ?? data.posts ?? [];
    return { posts: rawItems.map(normalizePost), total: data.total ?? 0 };
  },

  createPost: async (data: { content: string; imageUrl?: string }): Promise<Post> => {
    const response = await apiClient.post('/community/posts', {
      content: data.content,
      image_url: data.imageUrl || null,
    });
    return normalizePost(response.data);
  },

  likePost: async (postId: string): Promise<void> => {
    await apiClient.post(`/community/posts/${postId}/like`);
  },

  unlikePost: async (postId: string): Promise<void> => {
    await apiClient.delete(`/community/posts/${postId}/like`);
  },

  // Backend has no GET /comments endpoint — comments come embedded in the post detail
  getComments: async (postId: string): Promise<Comment[]> => {
    const response = await apiClient.get(`/community/posts/${postId}`);
    const rawComments: any[] = response.data.comments ?? [];
    return rawComments.map(normalizeComment);
  },

  addComment: async (postId: string, content: string): Promise<Comment> => {
    const response = await apiClient.post(`/community/posts/${postId}/comments`, { content });
    return normalizeComment(response.data);
  },

  followUser: async (userId: string): Promise<void> => {
    await apiClient.post(`/community/users/${userId}/follow`);
  },

  unfollowUser: async (userId: string): Promise<void> => {
    await apiClient.delete(`/community/users/${userId}/follow`);
  },

  getUserProfile: async (userId: string): Promise<UserPublicProfile> => {
    const response = await apiClient.get(`/community/users/${userId}/profile`);
    return normalizeProfile(response.data);
  },

  getUserPosts: async (userId: string, page = 1): Promise<{ posts: Post[]; total: number }> => {
    const response = await apiClient.get(`/community/users/${userId}/posts`, { params: { page, page_size: 20 } });
    const data = response.data;
    return {
      posts: (data.posts || []).map(normalizePost),
      total: data.total ?? 0,
    };
  },
};
